import { Box, IconButton, Button, Input, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../AuthContext";
import { tokens } from "../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Container, Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../assets/errors/nodata.png';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import DownloadIcon from '@mui/icons-material/Download';

const LeaveCredit = () => {
  const [leaves, setLeaves] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [leaveToDelete, setLeaveToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData, userID, userRole } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    user_id: '',
    leave_type: 'Annual',
    leaveCredit: 24,
    usedUpCredit: 0,
    leftCredit: 24,
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const [leavesResponse, usersResponse] = await Promise.all([
          getData('proccess.php', 'leaveCredits_V2'),
          getData('proccess.php', 'staffs')
        ]);

        if (leavesResponse.success) {
          setLeaves(leavesResponse.data);
        } else {
          toast.error(leavesResponse.message || "Failed to load leaves");
        }

        if (usersResponse.success) {
          setStaffs(usersResponse.data);
        } else {
          toast.error(usersResponse.message || "Failed to load staff");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch data");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const findStaff = (id) => {
    const staff = staffs.find(s => s.Id === id);
    if (!staff) {
      return `Staff member not found for`;
    } else {
      return [staff];
    }
  };

  const handleAddClick = () => {
    setFormData({
      user_id: '',
      leave_type: 'Annual',
      leaveCredit: 24,
        usedUpCredit: 0,
        leftCredit: 24,
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (leave) => {
    setSelectedLeave(leave);
    setFormData({
      user_id: leave.user_id || '',
      leave_type: leave.leave_type || 'Annual',
      leaveCredit: leave.leaveCredit || 24,
      usedUpCredit: leave.usedUpCredit || 0,
      leftCredit: leave.leftCredit || 24,
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (leave) => {
    setLeaveToDelete(leave);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteData('proccess.php', 'leaveCredits_V2', leaveToDelete.id);
      if (response.success) {
        toast.success("Leave deleted successfully");
        setLeaves(leaves.filter(l => l.id !== leaveToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete leave");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete leave");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setLeaveToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (selectedLeave) {
        // Update existing leave
        response = await updateData('proccess.php', 'leaveCredits_V2', formData, selectedLeave.id);
        if (response.success) {
          toast.success("Leave updated successfully");
          // Ensure each leave has an id
          const processedLeaves = response.data.map(leave => ({
            ...leave,
            id: leave.id // Make sure this matches your actual ID field
          }));
          setLeaves(processedLeaves);
        }
      } else {
        // Add new leave
        response = await addData('proccess.php', 'leaveCredits_V2', formData);
        if (response.success) {
          toast.success("Leave added successfully");
          // Ensure each leave has an id
          const processedLeaves = response.data.map(leave => ({
            ...leave,
            id: leave.id // Make sure this matches your actual ID field
          }));
          setLeaves(processedLeaves);
        }
      }
      
      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedLeave(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 50 },
    {
      field: "user_id", 
      headerName: "Full Name", 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const staff = staffs.find(s => s.Id === params.row?.user_id);
        return (
          <Box width="100%">
            {staff ? `${staff.FirstName} ${staff.LastName}` : 'Unknown'}
          </Box>
        );
      }
    },
    { field: "leave_type", headerName: "Leave Type", flex: 1, minWidth: 120 },
    // show staff leave credits
    {
      field: "leaveCredit", 
      headerName: "Leave Credits", 
      flex: 1, 
      minWidth: 120,
    },
    {
      field: "usedUpCredit", 
      headerName: "Used Credits", 
      flex: 1, 
      minWidth: 120,
    },
    {
      field: "leftCredit", 
      headerName: "Left Credits", 
      flex: 1, 
      minWidth: 120,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1, 
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" justifyContent={'center'} alignItems={'center'} height={'100%'}>
          <IconButton 
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton 
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.red[800] }}
            disabled={(params.row?.status === 1 || params.row?.status > 1) && (userRole === 'admin' || userRole === 'hr' || userRole === 'supervisor' || userRole === 'supperAdmin' || userRole === 'manager' || userRole === 'ceo')}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img 
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No data available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      {/* Add/Edit Dialog */}
      <Dialog 
        open={openEditDialog || openAddDialog} 
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedLeave(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>{selectedLeave ? "Edit Leave" : "Add New Leave"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{width: "100%", padding: "1rem"}}>
            <Box display={"flex"} gap={"1rem"} width={"100%"} mb={"1rem"} flexWrap="wrap">
              <TextField
                select
                style={{width: "100%"}}
                id="user_id"
                name="user_id"
                label="Staff Member"
                variant="standard"
                value={formData.user_id}
                onChange={handleFormChange}
                required
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                {staffs.map((staff) => (
                  <option key={staff.Id} value={staff.Id}>
                    {staff.FirstName} {staff.LastName}
                  </option>
                ))}
              </TextField>

              <TextField
                select
                style={{width: "100%"}}
                id="leave_type"
                name="leave_type"
                label="Leave Type"
                variant="standard"
                value={formData.leave_type}
                onChange={handleFormChange}
                required
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                <option key='Annual' value='Annual'>Annual</option>
                <option key='Sick' value='Sick'>Sick</option>
                <option key='Compassionate' value='Compassionate'>Compassionate</option>
                <option key='Study' value='Study'>Study</option>
                <option key='Maternity' value='Maternity'>Maternity</option>
                
              </TextField>
                <TextField
                    id="leaveCredit"
                    name="leaveCredit"
                    label="Leave Credits"
                    variant="standard"
                    type="number"
                    value={formData.leaveCredit}
                    onChange={handleFormChange} 
                    required
                    InputProps={{ inputProps: { min: 0 } }}
                />
                <TextField
                    id="usedUpCredit"
                    name="usedUpCredit"
                    label="Used Up Credits"
                    variant="standard"
                    type="number"
                    value={formData.usedUpCredit}
                    onChange={handleFormChange} 
                    required
                    InputProps={{ inputProps: { min: 0 } }}
                />
                <TextField
                    id="leftCredit"
                    name="leftCredit"
                    label="Left Credits"
                    variant="standard"
                    type="number"
                    value={formData.leftCredit}
                    onChange={handleFormChange} 
                    required
                    InputProps={{ inputProps: { min: 0 } }}
                />
                    
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenEditDialog(false);
            setOpenAddDialog(false);
          }}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            sx={{backgroundColor: colors.blue[400]}} 
            variant="contained" 
            endIcon={<SendIcon />}
          >
            {selectedLeave ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this leave request?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={confirmDelete}
            sx={{backgroundColor: colors.red[400]}} 
            variant="contained"
            startIcon={<DeleteForeverOutlinedIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent= {"space-between"}>
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight={"bold"}
            sx={{mb: "5px"}}
          >
            Staff Leaves Management
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              backgroundColor: colors.blue[400],
              '&:hover': {
                backgroundColor: colors.blue[500],
              }
            }}
          >
            Add Staff Leave Credits
          </Button>
        </Box>
      </Box>
      
      <Divider component="" />
      
      <Box sx={{ height: 'calc(100vh - 200px)', width: "100%" }}>
        <DataGrid
          rows={leaves || []}
          columns={columns}
          getRowId={(row) => row.id || Math.random().toString(36).substr(2, 9)}
          loading={loading}
          sx={{
            border: "none",
            backgroundColor: colors.primary[500],
            // Remove individual cell styling and move to global styles
            ".MuiDataGrid-cell": {
              borderBottom: "none",
            },
            // Define your status classes
            "& .status-pending .MuiDataGrid-cell": {
              backgroundColor: '#af928257', // Light orange for pending
              color: colors.orange[200], // Dark orange text for pending
              borderBottom: `.2px solid ${colors.orange[400]}`, // Orange left border for pending
            },
            "& .status-approved .MuiDataGrid-cell": {
              backgroundColor: '#6f968459', // Light green for approved
              color: colors.newTeal[200], // Dark green text for approved
              borderBottom: `.2px solid ${colors.green[400]}`, // Green left border for approved
            },
            "& .status-supervisor-approved .MuiDataGrid-cell": {
              backgroundColor: '#60a3d356', // Light blue for supervisor approved
              color: colors.indigo[200], // Dark blue text for supervisor approved
              borderBottom: `.2px solid ${colors.blue[400]}`, // Blue left border
            },
            "& .status-rejected .MuiDataGrid-cell": {
              backgroundColor: '#cf04223a', // Light red for rejected
              color: colors.red[100], // Dark red text for rejected
              borderBottom: `.2px solid ${colors.red[400]}`, // Red left border
            },
            "& .status-returned .MuiDataGrid-cell": {
              borderBottom: `.2px solid ${colors.indigo[400]}`, // Red left border
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: 'transparent',
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default LeaveCredit;